import { Inject, Injectable, OnDestroy } from '@angular/core';
import { StateService, Transition, UIRouter } from '@uirouter/core';
import { Subscription } from 'rxjs';

import * as DD from './digital-data/index';
import { WindowReference } from '../window';
import { CimaToken } from '../cima/token';
import { CimaCore } from '../cima/core';
import { Util } from '../util';
import { XmStore, XmStoreUtil } from '../store';
import { CONFIG_TOKEN, IsMobileCustomer, PaymentPlanName, UserType, XMPP_NAME } from 'core/constants';
import { User } from 'store/user/models';
import { Cart, CartItem } from 'store/cart/models';
import { ProtectionPlanVariant } from 'store/plan/models/variant/protection';
import { LineCollection, LineDetail, LineDevice } from 'store/line/models';
import { LineEligibility } from 'store/ndel/models';
import { DataLayer } from './data-layer';
import { IXMOptions } from 'core/interfaces';
import { CmsCompatibilityCheck, CmsEsimActivation } from 'store/cms/models';
import { DeviceType, UserRoles } from 'my-account/constants';
import { eligibilityStatus } from 'services/analytics/digital-data/constants';

@Injectable({
    providedIn: 'root'
})
export class Analytics implements OnDestroy {
    public customParams: object = {};
    public customPageName: string = '';
    public customEventAction: string = '';
    public customEventCategory: string = '';
    public line: LineDetail;
    public device: LineDevice;
    public compatibility: string = '';
    public lineCollection: LineCollection;
    public lineEligibility: LineEligibility;
    public eligibilityStatus: string;

    private cimaToken: CimaToken;
    private xmStore: XmStore;
    private breakpoint: string;
    private uiRouter: UIRouter;
    private user: User;
    private windowReference: WindowReference;
    private transitionSubscription: Subscription[] = [];
    private state: StateService;
    private cartId: string;
    private dataLayer: DataLayer;
    private config: IXMOptions;
    private cimaCore: CimaCore;

    // Stores all Items that were added to cart during the session. Used to identify removal Items
    private totalCartItems: TDigitalProductInfo[];
    // Stores current items in Cart
    private currentCartItems: TDigitalProductInfo[];

    constructor(cimaToken: CimaToken, dataLayer: DataLayer, state: StateService, windowReference: WindowReference, uiRouter: UIRouter, xmStore: XmStore, @Inject(CONFIG_TOKEN) config: IXMOptions, cimaCore: CimaCore) {
        Object.assign(this, {
            cimaToken,
            dataLayer,
            state,
            uiRouter,
            windowReference,
            xmStore,
            config,
            cimaCore
        });

        window.logAnalytics = false;
    }
    /* TBD - Track custom events, form tracking, Auth change */
    public init(): void {
        this.captureBreakpointInfo();
        this.captureUserInfo();
        this.buildCartData();
        this.setDefaultDigitalData(this.uiRouter.globals.current.name, this.uiRouter.globals.current.params);
    }

    public setupRouterListener(): void {
        // using onFinish function to chain a new Promise
        this.uiRouter.transitionService.onFinish({}, (transition: Transition) => {
            // always set pageName before any analytics "logic" begins
            this.setPageInfo(transition);

            // set customParams before pageInfo gets fired
            this.handleCustomRouteParams();

            // need to set the pageName immediately before firePageInfo on navigation success
            this.dataLayer._forceUpdate({ page: window.digitalDataLegacy.page });

            // the delay between sending customEvent through the data layer has millisecond delay getting through the event queue
            // by using the timeout, we can hold the route from completing ('success') given enough time before AB Tests resolve
            return new Promise((resolve: Function) => {
                setTimeout(resolve, 200); // delaying for the custom event (firePageInfo) to be processed
            });
        });

        // Fire Page Info after a successful transitions
        this.transitionSubscription.push(XmStoreUtil.subscribe(this.uiRouter.globals.success$, (transition: Transition) => {
            if (!(DD.PREVENT_PAGEVIEW_ROUTES.includes(transition.to().name))) {
                this.firePageInfo(transition);
            }
        }));

        this.transitionSubscription.push(XmStoreUtil.subscribe(this.uiRouter.globals.start$, () => {
            this.customPageName = '';
        }));
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.transitionSubscription);
    }

    /**
     * A function to send custom events which need to be tracked with specific 'DTM Tracking String'.
     * Call this function with following arguments:
     *
     * eventName: '' // Name of the event which matches the events constant to pull other data from constants.
     *
     * params: {} // optional custom params to inject either inside 'digitalData.event[].eventInfo' or 'digitalData' itself.
     *
     * overrideGlobalDigitalData: true | false
     * true -> will merge 'params' directly to 'digitalData'.
     * false -> will merge 'params' into 'digitalData.event[].eventInfo', which is default.
     *
     * sendingMultipleEvents: true | false   (will prevent the event overrides on a same page)
     * true -> if the same page is sending multiple events to the same data layer.
     * false -> if the same page is sending a single event, which is default.
     */
    public sendCustomEvent(eventName: string, params?: object, overrideGlobalDigitalData?: boolean, sendingMultipleEvents?: boolean): void {
        const newEventInfo: TCustomEvent = DD.CUSTOM_EVENTS.events.find((item: TCustomEvent) => item.eventName === eventName);

        if (newEventInfo) {
            this.trackCustomDigitalEvent(newEventInfo.eventName,
                newEventInfo.action,
                this.pipifiedRoute(this.uiRouter.globals.current.name, this.uiRouter.globals.current.params),
                params || {},
                overrideGlobalDigitalData,
                sendingMultipleEvents,
                newEventInfo.dtmEvent || DD.DTM_SEND_EVENT);
        }
    }

    public firePageInfo(transition: Transition): void {
        this.setPageInfo(transition);
        this.handleCustomRouteParams();
        this.dataLayer.trackPageInfo();

        this.logAnalytics('Analytics event: custom-pageLoad fired!');
    }

    public fireValidationError(errorText: string): void {
        const events: TDigitalEvent[] = window.digitalDataLegacy.event;
        if (events.some((eventData: TDigitalEvent) => (eventData.eventInfo.eventAction && Util.dotWalk(eventData.eventInfo, 'eventAction') === errorText))) {
            return;
        }

        this.sendCustomEvent('FE Error', { eventAction: errorText }, false, true);
    }

    public updateErrorMsg(errorMessage: string): void {
        window.digitalDataLegacy.page.attribute.errorMessage = errorMessage;
    }

    public captureBreakpointInfo(): void {
        this.windowReference.breakpoint.subscribe((breakpoint: Breakpoint) => {
            this.breakpoint = breakpoint.currentBreakpoint;
        });
    }

    public captureUserInfo(): void {
        XmStoreUtil.subscribe(this.xmStore.peek<User>(User, { returnUndefined: true }), (user: User) => {
            this.user = user;
            if (this.user && window.digitalDataLegacy.users) {
                const mobilePlanType: string = this.cimaToken.isLoggedIn ? this.user && this.user.account && this.user.account.level : undefined;

                window.digitalDataLegacy.users[0].profile[0].profileInfo.mobilePlanType = mobilePlanType;
            }
        });
    }

    public buildCartData(): void {
        this.transitionSubscription.push(XmStoreUtil.subscribe(this.xmStore.peek<Cart>(Cart, { returnUndefined: true }), (cart: Cart) => {
            if (cart !== undefined) {
                this.cartId = cart.id;

                if (cart.items) {
                    this.currentCartItems = cart.items.map((item: CartItem) => {
                        const prodInfo: TDigitalProductInfo = {
                            productID: item.variant.skuCode,
                            LOB: DD.LINE_OF_BUSINESS,
                            manufacturer: item.brand,
                            name: item.name,
                            color: item.color.name,
                            price: item.paymentPlanVariant.isFinanced ? item.variant.financePrice : item.variant.fullPrice,
                            quantity: item.quantity,
                            inStockFlag: !item.isOutOfStock,
                            cartItemId: item.id,
                            nickname: item.nickname
                        };

                        if (!item.isAccessory) {
                            const xmppVariant: ProtectionPlanVariant = item.productDetail.protectionPlanVariant;
                            prodInfo.storage = item.capacity || '';
                            prodInfo.paymentMethod = item.paymentPlanVariant.isFinanced ? PaymentPlanName.FINANCED : PaymentPlanName.PAY_IN_FULL;
                            prodInfo.phoneNumberType = item.phoneNumberType;
                            prodInfo.isByod = item.byod;
                            prodInfo.selectedPlan = item.ratePlanVariant.id || '';
                            prodInfo.xmppSelected = xmppVariant && xmppVariant.isValid;
                            prodInfo.xmppProductId = prodInfo.xmppSelected ? xmppVariant.id : '';
                            prodInfo.xmppName = prodInfo.xmppSelected ? XMPP_NAME : '';
                            prodInfo.xmppPrice = prodInfo.xmppSelected ? xmppVariant.monthlyPrice : 0;
                        }

                        return prodInfo;
                    });

                    this.totalCartItems = this.totalCartItems ?
                        this.totalCartItems.map((totalItem: TDigitalProductInfo) => this.currentCartItems.find((cartItem: TDigitalProductInfo) => totalItem.cartItemId === cartItem.cartItemId) || totalItem) : this.currentCartItems;
                } else {
                    this.currentCartItems = undefined;
                }
            }
        }));

    }
    
    public onLineEligibility(lineId: string): void {
        this.transitionSubscription.push(XmStoreUtil.subscribe(this.xmStore.peek<LineEligibility>(LineEligibility, {
            filters: { 'lineId.key': lineId }
        }), (lineEligibility: LineEligibility) => {
            if (lineEligibility) {
                this.lineEligibility = lineEligibility;
            }
        }));
    }

    private pipifiedRoute(stateName: string, params: object): string {
        if (!stateName) {
            return '';
        }

        // generating the URL given the transition state and params
        const nextUrl: string = (this.uiRouter.stateService.href(stateName, params, { inherit: false }) || '').replace(this.config.BASE_URL, '');
        let resultRoute: string = '';
        if (!window.location.href.includes('now')) {

            nextUrl.split('/').forEach((element: string) => {
            // Each ID will count pageload as a unique event, by skipping them we will get generic data of the states
                if (element.startsWith(DD.ID_IDENTIFER)) {
                    return;
                }

                // remove query parameter first if any
                let transformed: string = element.split('?')[0];
                // remove access token from URL
                transformed = transformed.split('#')[0];
                // lower-case each word
                transformed = transformed.toLocaleLowerCase();

                if (transformed && !transformed.endsWith('_id') && !/^[0-9]+$/.test(transformed)) {
                    resultRoute += `|${transformed}`;
                }
            });

            return resultRoute ? `xmobile|my-account${resultRoute}${this.customPageName ? '|' + this.customPageName : '' }` : 'xmobile|my-account';
        } else {
            const url: string = nextUrl.replace('now', '');
            url.split('/').forEach((element: string) => {
                // To remove line ID from page analytics
                if (element.match(/\d+/) || element === '') {
                    return;
                }
                // remove query parameter first if any
                let transformed: string = element.split('?')[0];
                // remove access token from URL
                transformed = transformed.split('#')[0];
                // lower-case each word
                transformed = transformed.toLocaleLowerCase();
    
                if (transformed && !transformed.endsWith('_id') && !/^[0-9]+$/.test(transformed)) {
                    resultRoute += `|${transformed}`;
                }
            });

            return resultRoute ? `resi|xfinitynow|mobile|my-account${resultRoute}` : 'resi|xfinitynow|mobile|my-account';

        }

    }

    private trackCustomDigitalEvent(eventName: string, eventAction: string, eventPage: string, params: object, overrideGlobalDigitalData?: boolean, _sendingMultipleEvents?: boolean, trackString: string = ''): void {
        const event: object = {
            eventName,
            eventPage,
            eventAction,
            eventMethod: trackString
        };

        if (overrideGlobalDigitalData) {
            this.dataLayer.sendUpdate(params);
        } else {
            Object.keys(params).forEach((key: string) => {
                event[key] = params[key];
            });
        }

        this.dataLayer.trackEvent(event);
    }

    /**
     * A function to merge custom route params (while transition to new state) into 'digitalData'.
     *
     * Define a new async function as 'resolveFn' in 'route.ts' file according to the requirements.
     * Fetch the data with 'await' and assign it to the 'analytics.customParams'.
     *
     * 'handleCustomRouteParams' function will be fired on successful transition in 'onload()' with custom params.
     */
    private handleCustomRouteParams(): void {
        if (Object.keys(this.customParams).length) {
            Util.mergeDeepWith(window.digitalDataLegacy, this.customParams);
        }
        this.customParams = {};
    }


    private setDefaultDigitalData(stateName: string, params: object): void {
        const paths: string[] = stateName && stateName.split('.') || [];
        const primaryCategory: string = paths[0] ? `${paths[0][0].toUpperCase()}${paths[0].substring(1)}` : '';
        const subCategory: string = paths[1] ? `${paths[1][0].toUpperCase()}${paths[1].substring(1)}` : '';
        const tertiaryCategory: string = paths[2] ? `${paths[2][0].toUpperCase()}${paths[2].substring(1)}` : '';
        const digitalData: TDigitalData = DD.DigitalData.forRoute(this.pipifiedRoute(stateName, params), primaryCategory, subCategory, {}, '', this.breakpoint, this.device, this.compatibility).content;

        // Add auth parameters
        const canNDEL: boolean = this.cimaToken.isLoggedIn && this.user && this.user.isXfinityMobileCustomer;
        const customerType: string = canNDEL ? DD.CUSTOMER_TYPE.existing : DD.CUSTOMER_TYPE.new;
        const authenticationStatus: string = this.cimaToken.isLoggedIn ? DD.AUTHENTICATION_STATUS.authenticated : DD.AUTHENTICATION_STATUS.unauthenticated;
        const userGUID: string = this.cimaToken.isLoggedIn ? this.cimaToken.decodedIdToken.tid : undefined;
        const custGUID: string = this.cimaToken.isLoggedIn ? this.cimaToken.decodedIdToken.sub : undefined;
        const mobilePlanType: string = this.cimaToken.isLoggedIn ? this.user && this.user.account && this.user.account.level : undefined;
        const isMobileCustomer: IsMobileCustomer = this.user && this.user.isXfinityMobileCustomer ? IsMobileCustomer.YES : IsMobileCustomer.NO;
        const accountType: string = this.pipifiedRoute(stateName, params).endsWith(DD.SECONDARY_USER) ? DD.ACCOUNT_TYPE.sub : this.cimaToken.isLoggedIn && DD.ACCOUNT_TYPE.main || undefined;
        const role: UserRoles = this.user ? this.user.account.role : undefined;
        digitalData.users[0].profile[0].profileInfo = {
            customerType,
            accountType,
            authenticationStatus,
            userGUID,
            custGUID,
            mobilePlanType,
            isMobileCustomer,
            role
        };
        this.trackLineTransferAnalytics(primaryCategory, subCategory, digitalData);
        this.setBusinessUnitType(digitalData);
        this.setSimType(digitalData);
        this.setDevicesInfo(subCategory, tertiaryCategory, digitalData);

        digitalData.cart.cartId = this.cartId;
        digitalData.cart.products.productInfo = this.currentCartItems;

        window.digitalDataLegacy = digitalData;
    }

    private trackLineTransferAnalytics(primaryCategory: string, subCategory: string, digitalData: TDigitalData): void {
        if (primaryCategory === DD.LINE_TRANSFER) { 
            digitalData.users[0].profile[0].profileInfo.TransferAccountType = subCategory.toLowerCase().replace(/-/i, ' ');
        }
    }

    private setBusinessUnitType(digitalData: TDigitalData): void {
        const isBusinessUser: boolean = this.cimaCore.isBusinessUser;
        const businessUnitType: string = isBusinessUser ? UserType.BUSINESS : UserType.RESIDENTIAL;
        digitalData.users[0].profile[0].profileInfo.BUtype = businessUnitType;
    }

    private setSimType(digitalData: TDigitalData): void {
        this.transitionSubscription.push(this.xmStore.peek<LineCollection>(LineCollection, { returnUndefined: true }).subscribe((result: LineCollection) => {
            if (this.state.params && this.state.params.line_id && this.state.params.device_id && result) {
                const device: LineDevice = result?.getLineByKey(this.state.params.line_id)?.getDeviceByKey(this.state.params.device_id);
                this.line = result?.getLineByKey(this.state.params.line_id);
                if (device) {
                    digitalData.users[0].profile[0].profileInfo.simType = device.simType;
                }
            }
        }));
    }

    private setPageInfo(transition: Transition): void {
        this.setDeviceInfo(transition);
        this.setDefaultDigitalData(transition.to().name, transition.params());

        this.logAnalytics(`Route: ${this.pipifiedRoute(transition.to().name, transition.params())}`);
        this.logAnalytics(JSON.stringify(window.digitalDataLegacy, undefined, '\t'));
    }

    private logAnalytics(data: string): void {
        if (window.logAnalytics) {
            /* eslint-disable  no-console */
            console.log(data);
            /* eslint-enable  no-console */
        }
    }

    private setDevicesInfo(subCategory: string, tertiaryCategory: string, digitalData: TDigitalData): void {
        this.transitionSubscription.push(this.xmStore.peek<LineCollection>(LineCollection, { returnUndefined: true }).subscribe((result: LineCollection) => {
            this.lineCollection = result;
        }));
        if (subCategory === DD.DEVICES && (tertiaryCategory === DD.DEVICE_DETAILS)) {
            if (this.state.params && this.state.params.line_id && this.state.params.device_id && this.lineCollection) {
                const device: LineDevice = this.lineCollection?.getLineByKey(this.state.params.line_id)?.getDeviceByKey(this.state.params.device_id);
                const line: LineDetail = this.lineCollection?.getLineByKey(this.state.params.line_id);
                this.onLineEligibility(line.id.key);
                if (this.lineEligibility && device.isDeviceUpgradeEligible && line.isLineEligibilityStatus) {
                    if (this.lineEligibility.upgradeEligible) {
                        this.eligibilityStatus = device.hasProtectionPlan ? eligibilityStatus.ELIGIBLE_NO_FEE_EARLY_UPGRADE : eligibilityStatus.ELIGIBLE_EARLY_UPGRADE;
                    } else {
                        this.eligibilityStatus = eligibilityStatus.ELIGIBLE_UPGRADE;
                    }
                } else {
                    this.eligibilityStatus = eligibilityStatus.INELIGIBLE_FOR_UPGRADE;
                }
                if (device) {
                    digitalData.page.pageInfo.device[0].deviceId = device.id;
                    digitalData.page.pageInfo.device[0].planCode = line.currentPlan.planCode;
                    digitalData.page.pageInfo.device[0].skuCode = device.productDetails.skuCode;
                    digitalData.page.pageInfo.device[0].eligibilityStatus = this.eligibilityStatus;
                }
            }
        } else if (subCategory === DD.DEVICES && (tertiaryCategory === DD.DEVICE_LANDING)) { 
            const deviceDetail: object[] = [{}];
            this.lineCollection?.lines.forEach((line: LineDetail) => {
                line.devices.forEach((device: LineDevice) => {
                    if (device.isDeviceUpgradeEligible && line.isLineEligibilityStatus) {
                        if (device.upgradeEligible) {
                            this.eligibilityStatus = device.hasProtectionPlan ? eligibilityStatus.ELIGIBLE_NO_FEE_EARLY_UPGRADE : eligibilityStatus.ELIGIBLE_EARLY_UPGRADE;
                        } else {
                            this.eligibilityStatus = eligibilityStatus.ELIGIBLE_UPGRADE;
                        }
                    } else {
                        this.eligibilityStatus = eligibilityStatus.INELIGIBLE_FOR_UPGRADE;
                    }
                    
                    deviceDetail.push({deviceId: line.id, skuCode: device.productDetails.skuCode, planCode: line.currentPlan.planCode, eligibilityStatus: this.eligibilityStatus});
                    digitalData.page.pageInfo.device = deviceDetail.filter( (item: object) => {
                        if (Object.keys(item).length !== 0) {
                            return true;
                        }
                        
                        return false;  
                    });
                });            
            });
        }
    }

    private setDeviceInfo(transition: Transition): void {
        if (this.state.params && this.state.params.line_id && this.state.params.device_id) {
            this.transitionSubscription.push(this.xmStore.peek<LineCollection>(LineCollection).subscribe((result: LineCollection) => {
                this.device = result?.getLineByKey(this.state.params.line_id)?.getDeviceByKey(this.state.params.device_id);
                if (this.device) {
                    this.setConfirmationInfo(transition);
                }
            }));
        }
    }

    private setConfirmationInfo(transition: Transition): void {
        if (transition.to().name.includes('byod.eligibility')) {
            this.transitionSubscription.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsEsimActivation>(CmsEsimActivation), (cms: CmsEsimActivation) => {
                if (this.device.isPhone) {
                    this.compatibility = cms.byodActivationErrors.content.find((contentData: ByodActivationErrors) => contentData.deviceType === DeviceType.PHONE).incompatible.header;
                } else {
                    this.compatibility = cms.byodActivationErrors.content.find((contentData: ByodActivationErrors) => contentData.deviceType === DeviceType.TABLET).incompatible.header;
                }
            }));
        } else if (transition.to().name.includes('details.compatibility.incompatible')) {
            this.transitionSubscription.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCompatibilityCheck>(CmsCompatibilityCheck), (response: CmsCompatibilityCheck) => {
                this.compatibility = response.incompatible.headline;
            }));
        } else if (transition.to().name.includes('details.compatibility.compatible')) {
            this.transitionSubscription.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCompatibilityCheck>(CmsCompatibilityCheck), (response: CmsCompatibilityCheck) => {
                this.compatibility = response.deviceCompatible.title;
            }));
        } else if (transition.to().name.includes('details.compatibility.psim')) {
            this.transitionSubscription.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCompatibilityCheck>(CmsCompatibilityCheck), (cmsCompatibilityCheck: CmsCompatibilityCheck) => {
                this.compatibility = cmsCompatibilityCheck.psimCompatibility.header;
            }));
        }
    }
}
